// Tile.ts
//
// Define the virtual parent tile description type.

import ActionResult from 'app/src/engine/action-results/ActionResult';
import Instruction from 'app/src/engine/instructions/Instruction';
import InstructionSegment from 'app/src/engine/instructions/InstructionSegment';
import { TileWallType } from 'app/src/engine/tiles/TileWalls';

export enum TileError {
	InvalidWallDescription,
}

export type TileLocation = {
	row: number;
	col: number;
};

type WallData = string[];

export default class Tile {
	public walls = new Set<TileWallType>();
	public allowsMultipleMovableItems = false;

	get tileName(): string {
		throw new Error('Subclasses must override.');
	}

	// When an action starts from this tile, optionally transform it.
	public transformInstruction(instruction: Instruction): Instruction {
		// By default, there's no transformation.
		return instruction;
	}

	// When an action segment starts from this tile, optionally transform it.
	public transformInstructionSegment(instructionSegment: InstructionSegment): InstructionSegment {
		// By default, there's no transformation.
		return instructionSegment;
	}

	// When a robot is leaving this tile using a given action result,
	// optionally transform it.
	public transformActionResultWhenLeaving(_actionResult: ActionResult): void {
		// By default, there's no transformation.
	}

	// When a robot is performing a given result while remaining on
	// this tile, optionally transform it.
	public transformActionResultWhenRemaining(_actionResult: ActionResult): void {
		// By default, there's no transformation.
	}

	// When a robot is performing a given result while entering this
	// tile, optionally transform it.
	public transformActionResultWhenEntering(_actionResult: ActionResult): void {
		// By default, there's no transformation.
	}

	// Based on an array of strings describing wall locations, create walls.
	public addWallsFromData(data: WallData): void {
		for (const wallName of data) {
			switch (wallName) {
				case 'Top':
					this.walls.add(TileWallType.Top);
					break;
				case 'Right':
					this.walls.add(TileWallType.Right);
					break;
				case 'Bottom':
					this.walls.add(TileWallType.Bottom);
					break;
				case 'Left':
					this.walls.add(TileWallType.Left);
					break;
				default:
					throw TileError.InvalidWallDescription;
			}
		}
	}

	public isEqualTo(_tile: Tile): boolean {
		throw new Error('Subclasses must implement.');
	}

	public clone(): Tile {
		throw new Error('Subclasses must implement.');
	}
}
